import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Info, TrendingUp, Shield, Calculator } from 'lucide-react';
import type { SupplierRatingThreshold } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { getGradeInfo, ratingBadgeStyle } from '../../pages/supplier-performance/utils';

interface RatingBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierName: string;
  combinedScore: number | null | undefined;
  otifMappedScore: number | null | undefined;
  ppmMappedScore: number | null | undefined;
  // Wave 3c+3e FIX-15 / Q31: the rating maps the OTIF percentage, so that is
  // what this screen shows. ``otdRate`` remains only as the fallback for a
  // payload with no OTIF figure on it.
  otifScorePercentage?: number | null;
  otdRate: number | null | undefined;
  totalPpm: number | null | undefined;
  /** CR-8215: the PPM the score maps (supplier liability, unassigned included).
   *  Shown in place of `totalPpm`, which covers every liability. */
  supplierLiabilityPpm?: number | null;
  // Wave 3c+3e R6: each input names its own period so the reader is not left
  // to assume. CR-8215: on the supplier card both are "Last 12 months".
  otifTimeframeLabel?: string;
  qualityTimeframeLabel?: string;
  /** CR-8215: the exact dates the period labels cover, shown on hover. */
  periodTitle?: string;
  // Wave 3c+3e FIX-19: the tenant's OWN grade bands. Absent, the modal shows no
  // legend at all — see the comment on the legend block below.
  ratingBands?: Array<{
    label: string;
    min_score: number;
    max_score: number;
    color?: string;
  }>;
  otifWeight?: number;
  ppmWeight?: number;
  /**
   * Customer review 11 Sep (item 2): the viewer's configured Grade
   * Thresholds (`useRatingBands()` in the caller). The badge grades with them
   * and, when `ratingBands` is not given, the legend lists them.
   */
  thresholds?: SupplierRatingThreshold[];
}

export default function RatingBreakdownModal({
  isOpen,
  onClose,
  supplierName,
  combinedScore,
  otifMappedScore,
  ppmMappedScore,
  otifScorePercentage,
  otdRate,
  totalPpm,
  supplierLiabilityPpm,
  otifTimeframeLabel,
  qualityTimeframeLabel,
  periodTitle,
  ratingBands,
  otifWeight = 70,
  ppmWeight = 30,
  thresholds = [],
}: RatingBreakdownModalProps) {
  const hasScore = combinedScore !== null && combinedScore !== undefined;
  const qualityLabel = useQualityMeasureLabel();
  // CR-8215: show the PPM the mapped score was built from; Total PPM only for
  // a payload that does not carry it.
  const hasScoredPpm = supplierLiabilityPpm !== null && supplierLiabilityPpm !== undefined;
  // Customer review 11 Sep (item 2): grade with the configured Grade
  // Thresholds (readable by every customer role now), not the hard-coded
  // fallback scale, so this badge matches the Supplier Performance table.
  const gradeInfo = getGradeInfo(combinedScore, thresholds);
  const legendBands = ratingBands && ratingBands.length > 0
    ? ratingBands
    : thresholds.map((b) => ({
        label: b.display_label || b.label || b.grade,
        min_score: parseFloat(b.min_score),
        max_score: parseFloat(b.max_score),
        color: b.effective_color || b.color,
      }));

  const formatScore = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(1);
  };

  const formatRate = (rate: number | null | undefined): string => {
    if (rate === null || rate === undefined) return 'N/A';
    return `${rate.toFixed(2)}%`;
  };

  // Two decimals, as the Supplier Performance page prints the same figure.
  const formatPpm = (ppm: number | null | undefined): string => {
    if (ppm === null || ppm === undefined) return 'N/A';
    return ppm.toFixed(2);
  };

  const period = (label?: string) => (label
    ? <span title={periodTitle}>{` · ${label}`}</span>
    : null);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-md"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => e.stopPropagation()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Rating Breakdown
          </DialogTitle>
          <DialogDescription>
            {supplierName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Combined Score Result */}
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-sm text-muted-foreground mb-2">Combined Score</div>
            {hasScore ? (
              <>
                <div className="text-4xl font-bold mb-2">{combinedScore?.toFixed(1)}</div>
                <Badge
                  className="text-sm px-3 py-1"
                  style={{
                    ...ratingBadgeStyle(gradeInfo.color),
                  }}
                >
                  {gradeInfo.label}
                </Badge>
              </>
            ) : (
              <div className="text-2xl font-semibold text-muted-foreground">N/A</div>
            )}
          </div>

          {/* Formula Explanation */}
          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Formula:</strong> Combined Score = (OTIF Score × {otifWeight}%) + (PPM Score × {ppmWeight}%)
              </div>
            </div>
          </div>

          {/* Contributing KPIs */}
          <div className="space-y-4">
            <div className="text-sm font-medium text-muted-foreground">Contributing KPIs</div>

            {/* OTIF/OTD Component */}
            <div className="p-3 border rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Delivery</span>
                </div>
                <span className="text-sm text-muted-foreground">Weight: {otifWeight}%</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  {/* Q31: the score beside this maps the OTIF percentage, so
                      show that. Falls back to the delivery rate — relabelled —
                      only where a payload carries no OTIF field. CR-8215: null
                      is a real N/A (no judged delivery in the period), shown as
                      such rather than as an OTD rate the score never read. */}
                  <div className="text-muted-foreground">
                    {otifScorePercentage === undefined
                      ? 'OTD Rate'
                      : <>OTIF Score{period(otifTimeframeLabel)}</>}
                  </div>
                  <div className="font-semibold">
                    {formatRate(
                      otifScorePercentage === undefined
                        ? otdRate
                        : otifScorePercentage,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Mapped Score</div>
                  <div className="font-semibold">{formatScore(otifMappedScore)}</div>
                </div>
              </div>
              {otifMappedScore !== null && otifMappedScore !== undefined && (
                <Progress value={otifMappedScore} className="h-2" />
              )}
              <div className="text-xs text-muted-foreground">
                Contribution: {formatScore(otifMappedScore)} × {otifWeight}% = {otifMappedScore !== null && otifMappedScore !== undefined ? ((otifMappedScore * otifWeight) / 100).toFixed(1) : 'N/A'}
              </div>
            </div>

            {/* PPM Component */}
            <div className="p-3 border rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <span className="font-medium">Quality ({qualityLabel})</span>
                </div>
                <span className="text-sm text-muted-foreground">Weight: {ppmWeight}%</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">
                    {hasScoredPpm ? `Supplier Liability ${qualityLabel}` : `${qualityLabel} Value`}
                    {period(qualityTimeframeLabel)}
                  </div>
                  <div className="font-semibold">{formatPpm(hasScoredPpm ? supplierLiabilityPpm : totalPpm)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Mapped Score</div>
                  <div className="font-semibold">{formatScore(ppmMappedScore)}</div>
                </div>
              </div>
              {ppmMappedScore !== null && ppmMappedScore !== undefined && (
                <Progress value={ppmMappedScore} className="h-2" />
              )}
              <div className="text-xs text-muted-foreground">
                Contribution: {formatScore(ppmMappedScore)} × {ppmWeight}% = {ppmMappedScore !== null && ppmMappedScore !== undefined ? ((ppmMappedScore * ppmWeight) / 100).toFixed(1) : 'N/A'}
              </div>
            </div>
          </div>

          {/* Score Mapping Legend — Wave 3c+3e FIX-19.
              This printed four HARD-CODED bands (Gold 85-100 / Silver 70-84 /
              Bronze 50-69 / Not Rated <50) while the badge above it comes from
              the tenant's own five-band SupplierRatingThreshold table. On the
              walked tenant that put a combined score of 80.0 in a Gold badge
              directly above a key saying Gold starts at 85 — the screen whose
              job is to explain the rating contradicting it, three lines apart.
              R-175's "rating scale stored five times", here a sixth copy.

              It was hard-coded because the supplier LIST payload carries no
              thresholds and /core/rating-thresholds/ is customer-admin only, so
              a plain customer user cannot fetch them (R-226). Until they are on
              the payload the modal shows NO legend rather than a false one. */}
          {/* Customer review 11 Sep (msg 2): the bands now reach every customer
              role through useRatingBands, so the legend is the real one. */}
          {legendBands.length > 0 && (
            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
              <div className="font-medium mb-2">Rating Thresholds</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {legendBands.map((band) => (
                  <div key={band.label} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: band.color ?? '#9CA3AF' }}
                    />
                    <span>{`${band.label}: ${band.min_score}-${band.max_score}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
